import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  FormControlLabel,
  Switch,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { GroupView } from '@app/views';
import { useAdminEngagementReportViewCreator } from '@app/view-creator/AdminCohortEngagementReportCardViewCreator';
import { getWeekStatus, WeekStatusKey } from '@utils/weekStatus';

interface EngagementReportDialogProps {
  open: boolean;
  group: GroupView;
  onClose: () => void;
}

const STATUS_COLORS: Record<WeekStatusKey, { bg: string; text: string; border: string }> = {
  success: { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  partial: { bg: '#fff8e1', text: '#f57f17', border: '#ffe082' },
  missed: { bg: '#fce4ec', text: '#c62828', border: '#ef9a9a' },
};

const getStatusLabel = (status: WeekStatusKey): string => {
  switch (status) {
    case 'success':
      return 'Goal met';
    case 'partial':
      return 'Partially met';
    case 'missed':
      return 'Missed';
  }
};

const headerSx = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: '#546e7a',
  borderBottom: '2px solid #e0e0e0',
  py: 1.5,
  whiteSpace: 'nowrap' as const,
};

export const EngagementReportDialog: React.FC<EngagementReportDialogProps> = ({ open, group, onClose }) => {
  const {
    data: engagementData,
    isLoading,
    filteredData,
    showAllUsers,
    setShowAllUsers,
    inactiveUsers,
    maxWeeks,
  } = useAdminEngagementReportViewCreator(group.id, open);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          margin: '40px',
          width: 'calc(100vw - 80px)',
          height: 'calc(100vh - 80px)',
          maxWidth: 'none',
          maxHeight: 'none',
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #e0e0e0',
          background: '#fafafa',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#263238' }}>
              Engagement Report
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#546e7a' }}>
                {group.name}
              </Typography>
              {engagementData && (
                <Chip label={`Week ${engagementData.currentWeekNumber}`} size="small" sx={{ fontWeight: 500 }} />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {inactiveUsers > 0 && (
              <FormControlLabel
                control={
                  <Switch checked={showAllUsers} onChange={(e) => setShowAllUsers(e.target.checked)} size="small" />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#546e7a' }}>
                    Show inactive users ({inactiveUsers})
                  </Typography>
                }
              />
            )}
          </Box>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
          {(['success', 'partial', 'missed'] as WeekStatusKey[]).map((status) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '3px',
                  backgroundColor: STATUS_COLORS[status].border,
                }}
              />
              <Typography variant="caption" sx={{ color: '#78909c' }}>
                {getStatusLabel(status)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        {filteredData ? (
          <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...headerSx,
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    backgroundColor: '#fafafa',
                    minWidth: 180,
                  }}
                >
                  Participant
                </TableCell>
                {Array.from({ length: maxWeeks }, (_, i) => (
                  <TableCell key={i} align="center" sx={{ ...headerSx, backgroundColor: '#fafafa', minWidth: 90 }}>
                    Week {i + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.cohortUsersWeeklyData.map((userData) => {
                const isInactive = !userData.cohortUser.isActive;
                return (
                  <TableRow
                    key={userData.cohortUser.id}
                    sx={{
                      opacity: isInactive ? 0.5 : 1,
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        backgroundColor: '#fff',
                        zIndex: 1,
                        borderRight: '1px solid #e0e0e0',
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#37474f' }}>
                          {userData.cohortUser.user.name || 'Unknown'}
                        </Typography>
                        {isInactive && (
                          <Chip
                            label="Inactive"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    {userData.weeks.map((week) => {
                      const status = getWeekStatus(week.progressCount, week.goalCount);
                      const colors = STATUS_COLORS[status];
                      return (
                        <TableCell key={week.week} align="center" sx={{ p: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                          <Tooltip title={getStatusLabel(status)} arrow placement="top">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `1px solid ${colors.border}`,
                                borderRadius: '6px',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                minWidth: 52,
                              }}
                            >
                              {week.progressCount}/{week.goalCount}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : isLoading ? (
          <Box sx={{ p: 3 }}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography
              sx={{
                color: 'text.secondary',
              }}
            >
              No data available
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', borderRadius: '8px' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
